import { Button, DatePicker } from "antd";
import dayjs, { Dayjs } from 'dayjs';
import TimePicker from "antd/lib/time-picker";
const { RangePicker } = DatePicker;
import type { ShortCut } from './DateTimeTypes';
import { useState, useEffect, useImperativeHandle, forwardRef } from "react";

export const assetModalShortCuts: ShortCut[] = [
    { label: 'Last 24 hours', value: [dayjs().subtract(1, 'day'), dayjs()] },
    { label: 'Last 7 days', value: [dayjs().subtract(7, 'day'), dayjs()] },
    { label: 'Last 30 days', value: [dayjs().subtract(30, 'day'), dayjs()] },
    { label: 'Last 3 months', value: [dayjs().subtract(3, 'month'), dayjs()] },
    { label: 'Last 6 months', value: [dayjs().subtract(6, 'month'), dayjs()] },
    { label: 'Last 12 months', value: [dayjs().subtract(12, 'month'), dayjs()] },
];

export const defaultShortCuts: ShortCut[] = [
    { label: 'Last 7 days', value: [dayjs().subtract(7, 'day'), dayjs()] },
    { label: 'Last 30 days', value: [dayjs().subtract(30, 'day'), dayjs()] },
    { label: 'Last 2 months', value: [dayjs().subtract(2, 'month'), dayjs()] },
    { label: 'Last 6 months', value: [dayjs().subtract(6, 'month'), dayjs()] },
    { label: 'Last 12 months', value: [dayjs().subtract(12, 'month'), dayjs()] },
];

type DateTimePickerProps = {
    shortCuts?: ShortCut[],
    value?: [Dayjs | null, Dayjs | null] | null;  
    onDateRangeChange?: (dates: [Dayjs, Dayjs] | null) => void,
    initialStartTime?: Dayjs,
    onStartTimeChange?: (time: Dayjs | null) => void,
    initialEndTime?: Dayjs,
    onEndTimeChange?: (time: Dayjs | null) => void,
    show?: boolean,
    allowClear?: boolean,
    initialShow?: boolean,
    onCancel?: () => void,
    onSave?: (dates: [Dayjs, Dayjs]) => void
};

const DateTimePicker = forwardRef<any, DateTimePickerProps>((props, ref) => {
    const {
        shortCuts = defaultShortCuts,
        value,
        onDateRangeChange,
        initialStartTime,
        onStartTimeChange,
        initialEndTime,
        onEndTimeChange,
        allowClear = true,
        initialShow = false,
        onCancel,
        onSave
    } = props;

    const [showCalendar, setShowCalendar] = useState(initialShow);
    const [dateRange, setDateRange] = useState<[Dayjs | null, Dayjs | null] | null>(value ?? null);
    const [startTime, setStartTime] = useState<Dayjs>(initialStartTime || value?.[0] || dayjs().startOf('day'));
    const [endTime, setEndTime] = useState<Dayjs>(initialEndTime || value?.[1] || dayjs().endOf('day').minute(55).second(0).millisecond(0));

    const updateDateAndTimeRange = ({ dates, start = startTime, end = endTime }: { dates?: [Dayjs | null, Dayjs | null] | null, start: Dayjs | null, end: Dayjs | null }) => {
        // Clear entries if no dates picked
        if (!dates || (!dates[0] && !dates[1])) {
            setDateRange(null);
            return;
        }

        // if only the first date is picked, allow partial
        if (dates[0] && !dates[1]) {
            setDateRange([dates[0], null]);
            return;
        }

        // if both dates are picked, apply times and ensure end is after start
        if (dates[0] && dates[1] && start && end) {
            let newStartTime = dayjs(dates[0]).hour(start.hour()).minute(start.minute()).second(0).millisecond(0);
            let newEndTime   = dayjs(dates[1]).hour(end.hour()).minute(end.minute()).second(0).millisecond(0);

            if (newEndTime.isSame(newStartTime, 'date') && newEndTime.isBefore(newStartTime)) {
            newEndTime = dayjs(newStartTime).hour(23).minute(55).second(0).millisecond(0);
            }

            setDateRange([newStartTime, newEndTime]);
            setStartTime(newStartTime);
            setEndTime(newEndTime);
            onDateRangeChange?.([newStartTime, newEndTime]);
        }
    };

    const handleSave = () => {
        onSave?.(dateRange as [Dayjs, Dayjs]);
        setShowCalendar(false);
    };

    const handleCancel = () => {
        setDateRange(value || null);
        setStartTime(value?.[0] || dayjs().startOf('day'))
        setEndTime(value?.[1] || dayjs().endOf('day').minute(55).second(0).millisecond(0));
        onCancel?.();
        setShowCalendar(false);
    };

    useEffect(() => {
        onStartTimeChange?.(startTime);
    }, [startTime]);

    useEffect(() => {
        onEndTimeChange?.(endTime);
    }, [endTime]);

    useImperativeHandle(ref, () => ({
        close() { // Method used to close the calendar from parent components
            setShowCalendar(false);
        }
    }));
    const hasFullRange = !!(dateRange && dateRange[0] && dateRange[1]);
    return (
        
        <RangePicker
            id="date-time-picker"
            className={`custom-outline-btn ${dateRange ? 'selected' : ''}`}
            presets={shortCuts}
            value={dateRange} 
            onCalendarChange={(dates) => {
                // fires on every change
                updateDateAndTimeRange({ dates: dates as [Dayjs | null, Dayjs | null] | null, start: startTime, end: endTime });
            }}
            onChange={(dates) => {
                // fires when range is completed or cleared
                // if cleared, dates === null
                if (!dates) {
                updateDateAndTimeRange({ dates: null, start: startTime, end: endTime });
                return;
                }
                // apply default times when both ends exist
                updateDateAndTimeRange({
                dates: dates as [Dayjs | null, Dayjs | null],
                start: dayjs().startOf('day'),
                end: dayjs().endOf('day').minute(55).second(0).millisecond(0),
                });
            }}
            open={showCalendar}
            onFocus={() => setShowCalendar(true)}
            onBlur={() => {
                if (!onCancel) setShowCalendar(false);
            }}
            allowClear={allowClear}
            renderExtraFooter={() => (
                <div data-testid="date-time-picker-extra-footer" id="date-time-picker-extra-footer">
                    <div
                        style={{
                            marginLeft: -132,
                            marginTop: -80,
                            marginBottom: 18,
                            width: 115,
                            textAlign: 'center',
                        }}
                    >
                        <div
                            style={{
                                borderTop: '1px solid #e8e6e6',
                                width: 112,
                                padding: 0,
                            }}
                        ></div>
                        <div className="flex column">
                            <span
                                className="font-bold"
                                style={{
                                    color: '#232B36',
                                    fontWeight: 'bold',
                                    marginBottom: -8,
                                }}
                            >
                                Start time
                            </span>
                            <div>
                                <TimePicker
                                    id="date-time-picker-start-time"
                                    disabled={!hasFullRange}
                                    value={startTime}
                                    placeholder="Start hour"
                                    format={'HH:mm'}
                                    style={{
                                        width: 100,
                                    }}
                                    onChange={(startTime: Dayjs | null) => {
                                        const value = startTime ?? dayjs().startOf('day')
                                        setStartTime(value)
                                        updateDateAndTimeRange({ dates: dateRange, start: value, end: endTime });
                                    }}
                                    minuteStep={5}
                                    allowClear={{
                                        clearIcon: (
                                            <span data-testid="start-time-clear-btn">
                                                ✕
                                            </span>
                                        ),
                                    }}
                                />
                            </div>
                        </div>
                        <div className="flex column">
                            <span
                                className="font-bold"
                                style={{
                                    color: '#232B36',
                                    fontWeight: 'bold',
                                    marginBottom: -8,
                                }}
                            >
                                End time
                            </span>
                            <div>
                                <TimePicker
                                    id="date-time-picker-end-time"
                                    disabled={!hasFullRange}
                                    value={endTime}
                                    placeholder="End hour"
                                    format={'HH:mm'}
                                    style={{
                                        width: 100,
                                    }}
                                    onChange={(endTime: Dayjs | null) => {
                                        const value = endTime ?? dayjs().endOf('day')
                                        setEndTime(value)
                                        updateDateAndTimeRange({ dates: dateRange, start: startTime, end: value });
                                    }}
                                    minuteStep={5}
                                    allowClear={{
                                        clearIcon: (
                                            <span data-testid="end-time-clear-btn">
                                                ✕
                                            </span>
                                        ),
                                    }}
                                />
                            </div>
                        </div>
                    </div>
                    <div
                        className="flex"
                        style={{
                            float: 'right',
                            marginTop: -60,
                            marginRight: 20,
                        }}
                    >
                        {onCancel && (
                            <Button
                                id="date-time-picker-cancel-button"
                                className="custom-outline-btn"
                                data-testid="date-time-picker-cancel-button"
                                onClick={handleCancel}
                                style={{
                                    width: '85px',
                                    height: '40px',
                                }}
                            >
                                Cancel
                            </Button>
                        )}
                        {onSave && (
                            <Button
                                id="date-time-picker-save-button"
                                data-testid="date-time-picker-save-button"
                                onClick={handleSave}
                                style={{
                                    marginLeft: '5px',
                                    width: '85px',
                                    height: '40px',
                                    backgroundColor: '#023E65',
                                    color: 'white',
                                }}
                                onMouseDown={(e) => (e.currentTarget.style.backgroundColor = '#00203C')}
                                onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#04628C')}
                                onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = '#023E65')}
                                onFocus={(e) => (e.currentTarget.style.border = '1px solid #023E65')}
                            >
                                Search
                            </Button>
                        )}
                    </div>
                </div>
            )}
        />
    );
});

export default DateTimePicker;